import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useVirtualizer } from "@tanstack/react-virtual";

import { Select, SearchableInput } from "@octane/spark";

import copy from "../copy.json";

// store value for "I will decide later"; distinct sentinel for the Select option keeps it separate from the empty/unselected state
const DECIDE_LATER_MAKE_ID = "none";
const DECIDE_LATER_OPTION_VALUE = "__decide_later__";
const ESTIMATED_OPTION_HEIGHT = 40;
const OPTIONS_VIEWPORT_HEIGHT = 240;
const OPTIONS_OVERSCAN = 10;

const normalizeMakeName = (name) =>
    name.trim().toLowerCase().replace(/\s+/g, " ");

const toSelectValue = (value) => {
    if (value == null) return "";
    if (value === DECIDE_LATER_MAKE_ID) return DECIDE_LATER_OPTION_VALUE;
    return value;
};

// Spark derives the selected button label from its direct children. This
// non-rendering child keeps that label available when the selected row is not
// currently mounted by the virtualizer.
const SelectedMakeValue = () => null;

const VirtualizedOptions = ({ items }) => {
    const optionsContainerRef = useRef(null);
    const virtualizer = useVirtualizer({
        count: items.length,
        getScrollElement: () => optionsContainerRef.current?.parentElement,
        estimateSize: () => ESTIMATED_OPTION_HEIGHT,
        getItemKey: (index) => items[index].key,
        initialRect: {
            width: 320,
            height: OPTIONS_VIEWPORT_HEIGHT,
        },
        overscan: OPTIONS_OVERSCAN,
    });

    useEffect(() => {
        const scrollElement = optionsContainerRef.current?.parentElement;
        if (scrollElement) {
            scrollElement.scrollTop = 0;
            virtualizer.scrollToOffset(0);
        }
    }, [items, virtualizer]);

    return (
        <div
            ref={optionsContainerRef}
            data-oid="virtualized-make-options"
            style={{
                height: `${virtualizer.getTotalSize()}px`,
                position: "relative",
                width: "100%",
            }}
        >
            {virtualizer.getVirtualItems().map((virtualItem) => {
                const item = items[virtualItem.index];

                return (
                    <div
                        key={item.key}
                        data-index={virtualItem.index}
                        ref={virtualizer.measureElement}
                        style={{
                            left: 0,
                            position: "absolute",
                            top: 0,
                            transform: `translateY(${virtualItem.start}px)`,
                            width: "100%",
                        }}
                    >
                        <Select.Option value={item.value} disabled={item.disabled}>
                            {item.label}
                        </Select.Option>
                    </div>
                );
            })}
        </div>
    );
};

VirtualizedOptions.propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            disabled: PropTypes.bool,
        })
    ).isRequired,
};

export const VirtualizedMakeSelect = ({
    value,
    options,
    disabled,
    onChange,
    hideNoneOption = false,
    isLoading = false,
}) => {
    const [searchPhrase, setSearchPhrase] = useState('');
    const normalizedSearchPhrase = normalizeMakeName(searchPhrase);

    // Normalize each make only when the API response changes. Searching then
    // compares precomputed strings instead of transforming all 1,000+ labels
    // on every keystroke.
    const normalizedOptions = useMemo(
        () => options.map((option) => ({
            option,
            normalizedName: normalizeMakeName(option.name),
        })),
        [options]
    );

    const handleSelection = (makeId) => {
        if (makeId === DECIDE_LATER_OPTION_VALUE) {
            setSearchPhrase('');
            return onChange({ id: DECIDE_LATER_MAKE_ID, name: DECIDE_LATER_MAKE_ID });
        }
        const makeSelected = options.find((option) => option.id === makeId);
        setSearchPhrase('');
        onChange(makeSelected);
    };

    const filteredOptions = useMemo(
        () => normalizedSearchPhrase
            ? normalizedOptions
                .filter(({ normalizedName }) =>
                    normalizedName.includes(normalizedSearchPhrase)
                )
                .map(({ option }) => option)
            : options,
        [normalizedOptions, normalizedSearchPhrase, options]
    );
    const showNoneOption = !hideNoneOption && !normalizedSearchPhrase;
    const showNoResults = !isLoading && !showNoneOption && filteredOptions.length === 0;

    const selectValue = toSelectValue(value);
    const selectedLabel = selectValue === DECIDE_LATER_OPTION_VALUE
        ? copy.fields.make.notSure
        : options.find((option) => option.id === selectValue)?.name;

    const virtualizedItems = useMemo(() => {
        if (isLoading) return [];

        if (showNoResults) {
            return [{
                key: "no-results",
                value: "__no_results__",
                label: copy.fields.make.noResults,
                disabled: true,
            }];
        }

        const makeItems = filteredOptions.map((option) => ({
            key: `make-${option.id}`,
            value: option.id,
            label: option.name,
            disabled: false,
        }));

        return showNoneOption
            ? [{
                key: "none",
                value: DECIDE_LATER_OPTION_VALUE,
                label: copy.fields.make.notSure,
                disabled: false,
            }, ...makeItems]
            : makeItems;
    }, [filteredOptions, isLoading, showNoResults, showNoneOption]);

    return (
        <Select
            id="select-make"
            label={copy.fields.make.label}
            placeholder={isLoading
                ? copy.fields.make.loading
                : copy.fields.make.placeholder}
            value={selectValue}
            disabled={disabled || isLoading}
            onChange={handleSelection}
            searchable
            searchComponent={
                <div
                    onKeyDownCapture={(event) => event.stopPropagation()}
                    onKeyUpCapture={(event) => event.stopPropagation()}
                >
                    <SearchableInput
                        id="make-search"
                        placeholder={copy.fields.make.searchPlaceholder}
                        value={searchPhrase}
                        onChange={(nextValue) => setSearchPhrase(nextValue)}
                        onSearch={() => {}}
                        onClear={() => setSearchPhrase("")}
                        variant="primary"
                    />
                </div>
            }
        >
            {selectedLabel && (
                <SelectedMakeValue value={selectValue}>
                    {selectedLabel}
                </SelectedMakeValue>
            )}
            <VirtualizedOptions items={virtualizedItems} />
        </Select>
    );
};

export const Make = (props) => <VirtualizedMakeSelect {...props} />;

Make.propTypes = {
    value: PropTypes.string,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
        })
    ).isRequired,
    disabled: PropTypes.bool,
    onChange: PropTypes.func,
    hideNoneOption: PropTypes.bool,
    isLoading: PropTypes.bool,
};

VirtualizedMakeSelect.propTypes = Make.propTypes;
